import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  ShieldCheck,
  UserCheck,
  AlertCircle,
  Mail,
  Phone,
  Building2,
  CheckCircle,
} from "lucide-react";
const TermsConditions = () => {
  const terms = [
    {
      icon: <UserCheck size={22} />,
      title: "Use of Information",
      text: "The information provided on this website is for general college, admission, course, facility, and contact-related purposes only.",
    },
    {
      icon: <CheckCircle size={22} />,
      title: "Enquiry Form",
      text: "Users must provide correct details while submitting the enquiry form. The college may contact users through phone, email, or WhatsApp for enquiry-related communication.",
    },
    {
      icon: <ShieldCheck size={22} />,
      title: "Privacy & Data",
      text: "Personal information submitted through the website will be used only for official communication and enquiry support.",
    },
    {
      icon: <AlertCircle size={22} />,
      title: "Content Accuracy",
      text: "The college tries to keep all information accurate and updated, but course details, timings, facilities, and policies may change from time to time.",
    },
  ];
  return (
    <section className="w-full bg-[#F6F8FC] py-12 md:py-16 overflow-hidden">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <span className="inline-flex items-center gap-2 bg-[#071A52] text-[#FFD700] px-5 py-2 rounded-full text-xs font-black uppercase tracking-wide mb-4 shadow">
              <FileText size={15} />
              Terms & Conditions
            </span>
            <h1 className="text-3xl md:text-5xl font-black text-[#071A52] leading-tight">
              Terms & Conditions
            </h1>
            <div className="w-24 h-1.5 bg-[#FFD700] rounded-full mx-auto mt-4 mb-4"></div>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Please read these terms carefully before using the website of Pt.
              Shrinivas Dwivedi Mahavidyalaya.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
            className="bg-white rounded-[30px] shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-[#071A52] via-[#0B1F5C] to-[#102B75] p-6 md:p-8 text-white relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-52 h-52 bg-[#FFD700]/20 rounded-full blur-3xl"></div>
              <div className="relative z-10 flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#FFD700] text-[#071A52] flex items-center justify-center shrink-0">
                  <Building2 size={28} />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-black">
                    Pt. Shrinivas Dwivedi Mahavidyalaya
                  </h2>
                  <p className="text-white/75 text-sm mt-2">
                    Arangi, Chandauli, Uttar Pradesh
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-5 mb-8">
                {terms.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-[#F6F8FC] border border-gray-100 rounded-2xl p-5 hover:shadow-md transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#071A52] text-[#FFD700] flex items-center justify-center mb-4">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-black text-[#071A52] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.text}
                    </p>
                  </motion.div>
                ))}
              </div>
              <div className="space-y-6 text-gray-700 text-sm md:text-base leading-relaxed">
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    1. Acceptance of Terms
                  </h3>
                  <p>
                    By using this website, you agree to follow the terms and
                    conditions mentioned on this page. If you do not agree with
                    these terms, please avoid using this website.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    2. Website Information
                  </h3>
                  <p>
                    The website provides information about the college,
                    available courses, facilities, activities, admission-related
                    details, gallery, and contact information. The content is
                    for informational purposes only.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    3. Admission & Course Details
                  </h3>
                  <p>
                    Course availability, admission process, eligibility,
                    important dates, and other academic details may change as
                    per college or university guidelines. Users should contact
                    the college office for final confirmation.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    4. User Responsibility
                  </h3>
                  <p>
                    Users are responsible for submitting correct and genuine
                    information through enquiry or contact forms. False or
                    misleading information may not be considered by the college.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    5. Communication Consent
                  </h3>
                  <p>
                    By submitting the enquiry form, users allow the college team
                    to contact them through phone, email, WhatsApp, or other
                    available communication methods regarding their enquiry.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    6. External Links
                  </h3>
                  <p>
                    This website may include external links such as Google Maps,
                    WhatsApp, or email links. The college is not responsible for
                    the content, security, or privacy practices of third-party
                    websites.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-black text-[#071A52] mb-2">
                    7. Changes to Terms
                  </h3>
                  <p>
                    The college reserves the right to update or modify these
                    terms and conditions at any time. Updated terms will be
                    available on this page.
                  </p>
                </div>
              </div>
              <div className="mt-8 bg-[#071A52] rounded-2xl p-5 md:p-6 text-white">
                <h3 className="text-xl font-black text-[#FFD700] mb-4">
                  Contact for Terms Related Queries
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p className="flex gap-3">
                    <Mail size={18} className="text-[#FFD700] shrink-0" />
                    <span>psndwivedi@gmail.com</span>
                  </p>
                  <p className="flex gap-3">
                    <Phone size={18} className="text-[#FFD700] shrink-0" />
                    <span>9451887217, 9450181867</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
export default TermsConditions;